import { BaseFactory } from 'src/services/entity-factory/FactoryInterface';
import Project from 'src/entities/Project';
import { Moment } from 'moment';
import ProjectImage from 'src/entities/ProjectImage';
import ProjectDocument from 'src/entities/ProjectDocument';
import { ApiInvestmentResponse } from 'src/services/entity-factory/factories/InvestmentFactory';
import { ApiCompanyResponse } from 'src/services/entity-factory/factories/CompanyFactory';
import { ApiProjectImageResponse } from 'src/services/entity-factory/factories/ProjectImageFactory';
import { ApiProjectTeamMemberResponse } from 'src/services/entity-factory/factories/ProjectTeamMemberFactory';
import { ApiOfflineInvestorResponse } from 'src/services/entity-factory/factories/OfflineInvestorFactory';
import OfflineInvestor from 'src/entities/OfflineInvestor';
import EnumTypes from 'src/services/EnumStates/EnumTypes';

export interface ApiProjectResponse {
  id: number;
  cashTarget: number;
  startDate: Moment;
  endDate: Moment;
  cash: number;
  capital: string;
  numberInvestors: number;
  company: ApiCompanyResponse;
  ticketValue: number;
  premoneyValuation: number;
  coInvestorsInvest: number;
  equity: number;
  sharePercentage: number;
  interested: number;
  description: string;
  images: ApiProjectImageResponse[];
  offlineInvestors: ApiOfflineInvestorResponse[];
  metrics: string;
  goToMarketDescription: string;
  goToMarketImage?: ApiProjectImageResponse;
  syndicateAgreement?: string;
  syndicateAgreementImage?: ApiProjectImageResponse;
  stakeholderAgreement?: string;
  stakeholderAgreementImage?: ApiProjectImageResponse;
  roundDetails: string;
  highlightInvestorName?: string;
  highlightInvestorDescription?: string;
  highlightInvestorImage?: ApiProjectImageResponse;
  environmentDescription: string;
  environmentImage?: ApiProjectImageResponse;
  documents: {
    id: number;
    name: string;
    url: string;
    imageUrl: string;
  }[];
  teamMembers: ApiProjectTeamMemberResponse[];
  invested?: ApiInvestmentResponse[];
  status: string;
  marketData?: string;
  dueDiligenceReport?: string;
  dueDiligenceStatus: string;
  activePayment?: boolean;
  formalizationStatus?: string;
}

interface ApiProjectCreateRequest {
}

export class ProjectFactory extends BaseFactory<Project> {
  public toEntity(args: ApiProjectResponse): Project {
    return new Project({
      id: args.id,
      cashTarget: args.cashTarget,
      startDate: args.startDate,
      endDate: args.endDate,
      cash: args.cash,
      capital: args.capital,
      numberInvestors: args.numberInvestors,
      company: this.entityFactoryFactory.companyFactory.toEntity(args.company),
      ticketValue: args.ticketValue,
      premoneyValuation: args.premoneyValuation,
      coInvestorsInvest: args.coInvestorsInvest,
      equity: args.equity,
      sharePercentage: args.sharePercentage,
      interested: args.interested,
      description: args.description,
      images: args.images ? args.images.map(image => new ProjectImage(image)) : [],
      offlineInvestors: args.offlineInvestors ?
        args.offlineInvestors.map(offlineInvestor => new OfflineInvestor(offlineInvestor)) :
        [],
      metrics: args.metrics,
      roundDetails: args.roundDetails,
      highlightInvestorName: args.highlightInvestorName || undefined,
      highlightInvestorDescription: args.highlightInvestorDescription || undefined,
      highlightInvestorImage: args.highlightInvestorImage ? new ProjectImage(args.highlightInvestorImage) : undefined,
      environmentDescription: args.environmentDescription,
      environmentImage: args.environmentImage ? new ProjectImage(args.environmentImage) : undefined,
      goToMarketDescription: args.goToMarketDescription,
      goToMarketImage: args.goToMarketImage ? new ProjectImage(args.goToMarketImage) : undefined,
      syndicateAgreement: args.syndicateAgreement || undefined,
      syndicateAgreementImage: args.syndicateAgreementImage ?
        new ProjectImage(args.syndicateAgreementImage) : undefined,
      stakeholderAgreement: args.stakeholderAgreement || undefined,
      stakeholderAgreementImage: args.stakeholderAgreementImage ?
        new ProjectImage(args.stakeholderAgreementImage) : undefined,
      documents: args.documents ? args.documents.map(document => new ProjectDocument(document)) : [],
      teamMembers: args.teamMembers ?
        args.teamMembers.map(teamMember => this.entityFactoryFactory.projectTeamMemberFactory.toEntity(teamMember)) :
        [],
      invested: args.invested ?
        args.invested.map(investment => this.entityFactoryFactory.investmentFactory.toEntity(investment)) :
        [],
      status: EnumTypes.getProjectStatusName(args.status),
      marketData: args.marketData || undefined,
      dueDiligenceReport: args.dueDiligenceReport || undefined,
      activePayment: args.activePayment || false,
      formalizationStatus: args.formalizationStatus ?
        EnumTypes.getProjectFormalizationStatusName(args.formalizationStatus) : '',
      dueDiligenceStatus: EnumTypes.getProjectDueDiligenceStatusName(args.dueDiligenceStatus),
    });
  }

  public toRequest(project: Project): ApiProjectCreateRequest {
    return project;
  }
}



// WEBPACK FOOTER //
// ./src/services/entity-factory/factories/ProjectFactory.tsx